import { AIChatSessionSummary } from 'models/AiDataModels';

import { ApiRouteService } from '../services';
import { HttpClient } from './HttpClient';

/**
 * Client for AI-related API operations
 */
export class AIClient {
  constructor(private httpClient: HttpClient) {
    this.getAllAiChatSessionsForPatient = this.getAllAiChatSessionsForPatient.bind(this);
    this.sendChatMessage = this.sendChatMessage.bind(this);
    this.getPatientSummary = this.getPatientSummary.bind(this);
  }

  /**
   * Get all AI chat sessions for a patient
   */
  public async getAllAiChatSessionsForPatient(patientId: number): Promise<AIChatSessionSummary[]> {
    const options = await this.httpClient.createStandardOptions('GET', null);
    const url = `${ApiRouteService.getCompumedApiBaseRoute()}/api/parseddata/patients/${patientId}/ai-chat-sessions`;
    const response = await this.httpClient.fetchAndParse<AIChatSessionSummary[]>(fetch(url, options));
    return response || [];
  }

  /**
   * Send a chat message to AI with context
   */
  public async sendChatMessage(request: AIChatRequest): Promise<AIChatResponse> {
    const options = await this.httpClient.createStandardOptions('POST', request);
    const url = `${ApiRouteService.getCompumedApiBaseRoute()}/api/ai/chat`;
    return await this.httpClient.fetchAndParse<AIChatResponse>(fetch(url, options));
  }

  /**
   * Get patient summary with optional template
   */
  public async getPatientSummary(patientId: number, promptTemplate?: string): Promise<string> {
    const options = await this.httpClient.createStandardOptions('POST', { promptTemplate });
    const url = `${ApiRouteService.getCompumedApiBaseRoute()}/api/ai/patients/${patientId}/summary`;
    return await this.httpClient.fetchAndParse<string>(fetch(url, options));
  }
}

/**
 * Request model for AI chat
 */
export interface AIChatRequest {
  patientId: number;
  message: string;
  context?: AIChatContext;
  includeFiles?: boolean;
  includeHistory?: boolean;
}

/**
 * Context for AI chat - provides relevant data for better responses
 */
export interface AIChatContext {
  worklistId?: number;
  worklistName?: string;
  selectedTags?: number[];
  visibleFileIds?: number[];
  currentView?: 'all' | 'filtered' | 'worklist';
  timeRange?: {
    start?: string;
    end?: string;
  };
}

/**
 * Response from AI chat
 */
export interface AIChatResponse {
  id: number;
  message: string;
  htmlContent?: string;
  isStreaming?: boolean;
  status: 'processing' | 'completed' | 'error';
  errorMessage?: string;
  contextUsed?: {
    filesAnalyzed: number;
    worklistContext?: string;
    tagsUsed?: string[];
  };
  timestamp: string;
}

/**
 * Chat message for display
 */
export interface ChatMessage {
  id: string;
  type: 'user' | 'ai' | 'system';
  content: string;
  htmlContent?: string;
  timestamp: Date;
  status?: 'sending' | 'sent' | 'processing' | 'completed' | 'error';
  contextUsed?: AIChatResponse['contextUsed'];
  errorMessage?: string;
}
